/* eslint-disable max-classes-per-file */
import * as Sentry from '@sentry/browser';
import { logEvent } from 'firebase/analytics';
import { useRef } from 'react';

import { recordAnalyticsToSnowflake } from '../api/api';
import { useAppDispatch } from '../app/hooks';
import { store } from '../app/store';
import { ANALYTICS } from '../firebase/firebase';
import { getUtmSource } from '../lib/utmManager';
import { getProductUUIDFromDomain } from '../utils/environments';
import { getPreapprovalCodeFromSession } from '../utils/functionUtils';
import {
  BATCH_SIZE,
  COUNT1,
  DEFAULT_SAMPLE_RATE,
  ERROR,
  EVENT,
  tagKeyFuncName,
  tagKeyPkgName,
  tagKeyReason,
} from './analyticsConsts';

export class AnalyticsEvent {
  consumerUUID: string;

  deviceUUID: string;

  language: string;

  readonly timestamp: number;

  readonly event: string;

  readonly userAgent: string;

  readonly appVersion: string;

  readonly parameters: { [key: string]: string };

  readonly timezone: string;

  readonly platform: string;

  constructor(eventIn: string, parametersIn: { [key: string]: string }) {
    this.timestamp = Math.floor(Date.now() / 1000);
    this.event = eventIn;
    this.parameters = parametersIn;
    this.userAgent = navigator?.userAgent || '';
    this.consumerUUID = '';
    this.deviceUUID = '';
    this.appVersion = '';
    this.language = '';
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.platform = 'web';
  }

  // eslint-disable-next-line class-methods-use-this
  setLanguage(languageIn: string) {
    this.language = languageIn;
  }

  setConsumerUUID(consumerIn: string) {
    this.consumerUUID = consumerIn;
  }

  setDeviceUUID(deviceIn: string) {
    this.deviceUUID = deviceIn;
  }
}

export class CountMetric {
  readonly package: string;
  readonly where: string;
  readonly count: number;
  readonly tags: string[];
  readonly sampleRate: number;

  constructor(
    pkg: string,
    where: string,
    count: number,
    tags: string[],
    sampleRate: number
  ) {
    this.package = pkg;
    this.where = where;
    this.count = count;
    this.tags = tags;
    this.sampleRate = sampleRate;
  }
}

export default class AnalyticsUtil {
  eventList: Array<AnalyticsEvent>;

  countMetrics: Array<CountMetric>;

  deviceUUID: any;

  consumerUUID: any;

  dispatch = useAppDispatch;

  constructor() {
    this.eventList = [];
    this.deviceUUID = '';
    this.consumerUUID = '';
    this.countMetrics = [];
  }

  writeEvent(
    eventIn: string,
    parameters: { [key: string]: string },
    analyticsObj: any
  ) {
    const puuid = store.getState().merchantDataSelector.productUUID || '';
    const utmSource = getUtmSource(puuid);
    const preapprovalCode = getPreapprovalCodeFromSession();

    if (utmSource) {
      parameters.utm_source = utmSource;
    }

    if (preapprovalCode) {
      parameters.preapproval_code = preapprovalCode;
    }

    if (puuid !== '') {
      parameters.product_uuid = puuid;
    } else {
      getProductUUIDFromDomain(window.location.hostname.toLowerCase());
    }

    const parametersIsEmpty = Object.keys(parameters).length === 0;
    logEvent(analyticsObj, eventIn, parametersIsEmpty ? undefined : parameters);

    this.eventList.push(new AnalyticsEvent(eventIn, parameters));

    if (this.eventList.length === BATCH_SIZE) {
      this.writeStoredEvents();
      this.eventList = [];
    }
  }

  writeStoredEvents() {
    if (!this.eventList.length && !this.countMetrics.length) {
      return; // Exit if there are no events or metrics to process
    }

    const enhancedEvents = this.eventList.map((event) => {
      const consumerUUID =
        store.getState().consumerInfoSelector.consumerUUID || '';
      const deviceUUID =
        store.getState().apiScreenScreenSelector.deviceID || '';
      const languageCode = store.getState().languageSelector.code || '';

      return {
        ...event,
        consumerUUID,
        deviceUUID,
        languageCode,
      };
    });

    const analyticsRequest = {
      events: enhancedEvents,
      metrics: this.countMetrics.length
        ? { countMetrics: this.countMetrics }
        : undefined,
    };

    const analyticsRequestData = JSON.stringify(analyticsRequest);
    if (enhancedEvents.length || this.countMetrics.length) {
      recordAnalyticsToSnowflake({ analyticsRequestData });
    }

    this.eventList = [];
    this.countMetrics = [];
  }

  funcError(pkgName: string, funcName: string, reason: string) {
    const tags = [
      tagKeyPkgName + pkgName,
      tagKeyFuncName + funcName,
      tagKeyReason + reason,
    ];

    Sentry.captureException(new Error(`${pkgName} ${funcName}`), {
      tags: {
        pkgName,
        funcName,
        reason,
        consumerUUID: store.getState().consumerInfoSelector.consumerUUID ?? '',
        deviceUUID: store.getState().apiScreenScreenSelector.deviceID ?? '',
      },
    });

    const countMetric = new CountMetric(
      ERROR,
      funcName,
      COUNT1,
      tags,
      DEFAULT_SAMPLE_RATE
    );
    this.countMetrics.push(countMetric);
    this.writeStoredEvents();
  }
}

/**
 * Hook to log an analytics event only once.
 *
 * @returns a function that takes an analytics event and logs it if it hasn't been logged before.
 */
export const useLogEventOnce = () => {
  const loggedEventsRef = useRef<EVENT[]>([]);

  return (
    event: EVENT,
    parameters?: { [key: string]: string },
    description?: string
  ) => {
    if (loggedEventsRef.current.includes(event)) return;
    loggedEventsRef.current.push(event);
    ANALYTICS.logEvent(event, parameters, description);
  };
};
